import { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Upload, FileText, Loader2, CheckCircle2, AlertCircle, ChevronRight, ChevronLeft } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FreshClinicsReportFormV1 = ({ onSubmit, quarter, year, month }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);

  // Step 1: Auto-selected Previous Reports & Valuation Excel
  const [autoSelectedReports, setAutoSelectedReports] = useState([]);
  const [autoSelectLoading, setAutoSelectLoading] = useState(false);
  
  const [valuationExcelFile, setValuationExcelFile] = useState(null);
  const [valuationExcelData, setValuationExcelData] = useState(null);
  const [valuationExcelExtracted, setValuationExcelExtracted] = useState(false);

  // Step 2: Additional Documents
  const [additionalDocs, setAdditionalDocs] = useState([]);

  // Step 3: Data Fields - with status tracking
  const [section1Data, setSection1Data] = useState({
    // Executive Summary & Fair Value
    previousQuarterSharePrice1: { value: '', status: 'empty' },
    previousQuarterSharePrice2: { value: '', status: 'empty' },
    totalJAADEShares: { value: '', status: 'empty' },
    totalSharesOnIssue: { value: '', status: 'empty' },
    jaadeOwnershipPercent: { value: '', status: 'empty' },
    currentCashBalance: { value: '', status: 'empty' },
    totalDebt: { value: '', status: 'empty' },
    initialInvestmentDate: { value: '', status: 'empty' },
    initialCost: { value: '', status: 'empty' },
    totalCapitalReturned: { value: '', status: 'empty' }
  });

  const [section2Data, setSection2Data] = useState({
    // Trading Multiple Evolution (quarterly data arrays)
    historicalEV: { value: '', status: 'empty' },
    nitroNTMRevenue: { value: '', status: 'empty' },
    australianSaaSMultiples: { value: '', status: 'empty' },
    globalDirectCompsMultiples: { value: '', status: 'empty' }
  });

  const [section3Data, setSection3Data] = useState({
    // Review of Operational Factors (qualitative)
    operationalMilestones: { value: '', status: 'empty' },
    strategicInitiatives: { value: '', status: 'empty' }
  });

  const [section4Data, setSection4Data] = useState({
    // Financial Performance & Key SaaS Metrics
    arr: { value: '', status: 'empty' },
    totalRevenue: { value: '', status: 'empty' },
    yoyGrowthRate: { value: '', status: 'empty' },
    subscriptionRevenue: { value: '', status: 'empty' },
    perpetualRevenue: { value: '', status: 'empty' },
    servicesRevenue: { value: '', status: 'empty' },
    operatingEBITDA: { value: '', status: 'empty' },
    cashEBITDA: { value: '', status: 'empty' },
    ebitdaMarginActual: { value: '', status: 'empty' },
    ebitdaMarginBudget: { value: '', status: 'empty' },
    nrr: { value: '', status: 'empty' },
    grr: { value: '', status: 'empty' },
    ruleOf40: { value: '', status: 'empty' }
  });

  const [section5Data, setSection5Data] = useState({
    // Scorecard & Triangulation
    peerBenchmarks: { value: '', status: 'empty' },
    dcfWACC: { value: '20', status: 'filled' },
    dcfTerminalGrowth: { value: '3', status: 'filled' },
    dcfExitMultiple: { value: '10-12.5', status: 'filled' },
    lreForecastedEBITDA: { value: '', status: 'empty' },
    lreExitMultiple: { value: '20', status: 'filled' },
    lreLiquidityDiscount: { value: '30', status: 'filled' },
    lreControlPremium: { value: '15', status: 'filled' },
    lreDiscountRate: { value: '25', status: 'filled' }
  });

  const [section6Data, setSection6Data] = useState({
    // Trading Multiple Evolution Chart Data (10 quarters)
    quarters: [
      { date: '', ev: '', nitroMultiple: '', compsMultiple: '', status: 'empty' },
      { date: '', ev: '', nitroMultiple: '', compsMultiple: '', status: 'empty' },
      { date: '', ev: '', nitroMultiple: '', compsMultiple: '', status: 'empty' },
      { date: '', ev: '', nitroMultiple: '', compsMultiple: '', status: 'empty' },
      { date: '', ev: '', nitroMultiple: '', compsMultiple: '', status: 'empty' },
      { date: '', ev: '', nitroMultiple: '', compsMultiple: '', status: 'empty' },
      { date: '', ev: '', nitroMultiple: '', compsMultiple: '', status: 'empty' },
      { date: '', ev: '', nitroMultiple: '', compsMultiple: '', status: 'empty' },
      { date: '', ev: '', nitroMultiple: '', compsMultiple: '', status: 'empty' },
      { date: '', ev: '', nitroMultiple: '', compsMultiple: '', status: 'empty' }
    ]
  });

  // Section document uploads (for AI extraction)
  const [sectionDocs, setSectionDocs] = useState({
    section1: [],
    section2: [],
    section3: [],
    section4: [],
    section5: [],
    section6: []
  });

  // Step 4: AI Summaries
  const [aiSummaries, setAiSummaries] = useState({
    strategicInitiatives: '',
    operationalMilestones: '',
    overallUpdate: ''
  });
  const [summariesGenerated, setSummariesGenerated] = useState(false);
  const [summariesLoading, setSummariesLoading] = useState(false);
  const [userComments, setUserComments] = useState({
    strategicInitiatives: '',
    operationalMilestones: '',
    overallUpdate: ''
  });
  const [qualitativeStoryUpdates, setQualitativeStoryUpdates] = useState('');

  // Step 5: Report Generation
  const [aiAnalysisComplete, setAiAnalysisComplete] = useState(false);
  const [aiNeedsMoreData, setAiNeedsMoreData] = useState(false);
  const [aiDataRequest, setAiDataRequest] = useState('');
  const [aiValuation, setAiValuation] = useState(null);
  const [additionalDataText, setAdditionalDataText] = useState('');
  const [additionalDataFiles, setAdditionalDataFiles] = useState([]);
  const [reportGenerating, setReportGenerating] = useState(false);
  const [reportGenerated, setReportGenerated] = useState(false);
  const reportTriggeredRef = useRef(false);

  // Step 5: Financials screenshot (optional before generation)
  const [financialsScreenshot, setFinancialsScreenshot] = useState(null);
  const [keyFinancialsScreenshot, setKeyFinancialsScreenshot] = useState(null);

  // Helper function to safely extract text from string or object
  const extractText = (data) => {
    if (typeof data === 'string') {
      return data;
    }
    if (typeof data === 'object' && data !== null) {
      // If it's an object, try to extract meaningful text
      if (data.text) return data.text;
      if (data.content) return data.content;
      if (data.summary) return data.summary;
      // If it has multiple text fields, combine them
      const textFields = [];
      if (data.summary) textFields.push(data.summary);
      if (data.assumptions) {
        if (Array.isArray(data.assumptions)) {
          textFields.push(...data.assumptions);
        } else {
          textFields.push(data.assumptions);
        }
      }
      if (data.calculation_steps) {
        if (Array.isArray(data.calculation_steps)) {
          textFields.push(...data.calculation_steps);
        } else {
          textFields.push(data.calculation_steps);
        }
      }
      if (data.adjustments_and_sensitivities) {
        textFields.push(data.adjustments_and_sensitivities);
      }
      if (textFields.length > 0) {
        return textFields.join('\n\n');
      }
      // Last resort: stringify the object
      return JSON.stringify(data, null, 2);
    }
    return '';
  };

  const steps = [
    { num: 1, title: 'Valuation Data', desc: 'Upload Excel & review' },
    { num: 2, title: 'Financial Data Review', desc: 'All valuation data' },
    { num: 3, title: 'Additional Documents', desc: 'Up to 5 documents' },
    { num: 4, title: 'AI Summaries', desc: 'AI-generated insights' },
    { num: 5, title: 'Generate Report', desc: 'Final report' }
  ];

  // Auto-select previous reports when month/year are available
  useEffect(() => {
    if (month && year) {
      const fetchAutoSelected = async () => {
        setAutoSelectLoading(true);
        try {
          // Convert month name to month number
          const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                              'July', 'August', 'September', 'October', 'November', 'December'];
          const monthIndex = monthNames.indexOf(month);
          const monthNum = monthIndex !== -1 ? String(monthIndex + 1) : '6'; // Default to June if not found
          
          const res = await axios.get(`${API}/reports/fresh-clinics-v1/previous-reports/auto-select`, {
            params: { month: monthNum, year }
          });
          setAutoSelectedReports(res.data || []);
        } catch (err) {
          console.error('Error auto-selecting reports:', err);
        } finally {
          setAutoSelectLoading(false);
        }
      };
      fetchAutoSelected();
    }
  }, [month, year]);

  const handleAdditionalDocUpload = (files) => {
    const fileArray = Array.from(files);
    if (additionalDocs.length + fileArray.length > 5) {
      toast.error('Maximum 5 additional documents allowed');
      return;
    }
    setAdditionalDocs(prev => [...prev, ...fileArray]);
    toast.success(`${fileArray.length} document(s) added`);
  };

  const handleValuationExcelUpload = async (file) => {
    if (!file) return;
    
    setValuationExcelFile(file);
    setExtracting(true);
    toast.info('Extracting financial data from Excel file...');

    try {
      const formData = new FormData();
      formData.append('valuation_excel', file);
      formData.append('quarter', quarter);
      formData.append('year', year);

      const response = await axios.post(`${API}/reports/fresh-clinics-v1/extract-valuation-excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000
      });

      setValuationExcelData(response.data.extracted_data);
      setValuationExcelExtracted(true);

      // Populate sections H-L from section_data
      const sd = response.data.section_data;
      if (sd) {
        // Section H -> section1Data
        if (sd.section1 && Object.keys(sd.section1).length > 0) {
          setSection1Data(prev => {
            const updated = { ...prev };
            Object.keys(sd.section1).forEach(key => {
              if (updated[key] !== undefined) {
                const item = sd.section1[key];
                updated[key] = {
                  value: typeof item === 'object' ? (item.value || '') : item,
                  status: typeof item === 'object' ? (item.status || 'filled') : 'filled'
                };
              }
            });
            return updated;
          });
        }
        // Section I -> section2Data
        if (sd.section2 && Object.keys(sd.section2).length > 0) {
          setSection2Data(prev => {
            const updated = { ...prev };
            Object.keys(sd.section2).forEach(key => {
              if (updated[key] !== undefined) {
                const item = sd.section2[key];
                updated[key] = {
                  value: typeof item === 'object' ? (item.value || '') : item,
                  status: typeof item === 'object' ? (item.status || 'filled') : 'filled'
                };
              }
            });
            return updated;
          });
        }
        // Section J -> section4Data
        if (sd.section4 && Object.keys(sd.section4).length > 0) {
          setSection4Data(prev => {
            const updated = { ...prev };
            Object.keys(sd.section4).forEach(key => {
              if (updated[key] !== undefined) {
                const item = sd.section4[key];
                updated[key] = {
                  value: typeof item === 'object' ? (item.value || '') : item,
                  status: typeof item === 'object' ? (item.status || 'filled') : 'filled'
                };
              }
            });
            return updated;
          });
        }
        // Section K -> section5Data
        if (sd.section5 && Object.keys(sd.section5).length > 0) {
          setSection5Data(prev => {
            const updated = { ...prev };
            Object.keys(sd.section5).forEach(key => {
              if (updated[key] !== undefined) {
                const item = sd.section5[key];
                updated[key] = {
                  value: typeof item === 'object' ? (item.value || '') : item,
                  status: typeof item === 'object' ? (item.status || 'filled') : 'filled'
                };
              }
            });
            return updated;
          });
        }
        // Section L -> section6Data (quarters array)
        if (sd.section6 && sd.section6.quarters && Array.isArray(sd.section6.quarters)) {
          const extractedQuarters = sd.section6.quarters;
          setSection6Data(prev => {
            const newQuarters = prev.quarters.map((q, idx) => {
              if (idx < extractedQuarters.length) {
                const eq = extractedQuarters[idx];
                return {
                  date: eq.date || '',
                  ev: eq.ev || '',
                  nitroMultiple: eq.nitroMultiple || '',
                  compsMultiple: eq.compsMultiple || '',
                  status: eq.status || 'filled'
                };
              }
              return q;
            });
            return { quarters: newQuarters };
          });
        }
      }

      toast.success('✓ Valuation data extracted! Review it in Step 2.');
    } catch (error) {
      console.error('Excel extraction error:', error);
      toast.error('Failed to extract data from Excel file. You can still proceed manually.');
      setValuationExcelExtracted(false);
    } finally {
      setExtracting(false);
    }
  };

  const handleSectionDocUpload = async (section, files) => {
    const fileArray = Array.from(files);
    if (fileArray.length > 5) {
      toast.error('Maximum 5 documents per section');
      return;
    }
    
    setSectionDocs(prev => ({
      ...prev,
      [section]: fileArray
    }));

    // Trigger AI extraction
    await extractDataFromDocs(section, fileArray);
  };

  const extractDataFromDocs = async (section, files) => {
    setExtracting(true);
    toast.info('Extracting data from documents...');

    try {
      const formData = new FormData();
      formData.append('section', section);
      formData.append('quarter', quarter);
      formData.append('year', year);
      
      files.forEach((file, index) => {
        formData.append(`file_${index}`, file);
      });

      const response = await axios.post(`${API}/reports/fresh-clinics-v1/extract`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });

      const extractedData = response.data.extracted_data;
      
      // Update the appropriate section with extracted data
      updateSectionWithExtractedData(section, extractedData);
      
      toast.success('Data extracted successfully!');
    } catch (error) {
      console.error('Extraction error:', error);
      toast.error('Failed to extract data from documents');
    } finally {
      setExtracting(false);
    }
  };

  const updateSectionWithExtractedData = (section, extractedData) => {
    // Map extracted data to section fields with status
    const updateSection = (setter, data) => {
      setter(prev => {
        const updated = { ...prev };
        Object.keys(data).forEach(key => {
          if (updated[key]) {
            updated[key] = {
              value: data[key].value,
              status: data[key].status // 'filled', 'ambiguous', 'missing'
            };
          }
        });
        return updated;
      });
    };

    switch (section) {
      case 'section1':
        updateSection(setSection1Data, extractedData);
        break;
      case 'section2':
        updateSection(setSection2Data, extractedData);
        break;
      case 'section3':
        updateSection(setSection3Data, extractedData);
        break;
      case 'section4':
        updateSection(setSection4Data, extractedData);
        break;
      case 'section5':
        updateSection(setSection5Data, extractedData);
        break;
      case 'section6':
        // Handle quarters array for Trading Multiple Evolution
        if (extractedData.quarters && Array.isArray(extractedData.quarters)) {
          setSection6Data({
            quarters: extractedData.quarters.map(q => ({
              date: q.date || '',
              ev: q.ev || '',
              nitroMultiple: q.nitroMultiple || '',
              compsMultiple: q.compsMultiple || '',
              status: q.status || 'missing'
            }))
          });
        }
        break;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'filled': return 'border-green-500 bg-green-50';
      case 'ambiguous': return 'border-orange-500 bg-orange-50';
      case 'missing': return 'border-red-500 bg-red-50';
      default: return '';
    }
  };

  const handleFieldChange = (section, field, value) => {
    const setter = {
      section1: setSection1Data,
      section2: setSection2Data,
      section3: setSection3Data,
      section4: setSection4Data,
      section5: setSection5Data
    }[section];

    setter(prev => ({
      ...prev,
      [field]: {
        value,
        status: value ? 'filled' : 'empty'
      }
    }));
  };

  const canProceedToNextStep = () => {
    switch (currentStep) {
      case 1:
        // Must have valuation Excel uploaded and extraction complete
        return valuationExcelFile && 
               !extracting && 
               valuationExcelExtracted;
      case 2:
        return true; // Can proceed even with missing fields - user can fill manually
      case 3:
        return true; // Additional docs are optional
      case 4:
        return summariesGenerated;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (canProceedToNextStep()) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length));
    } else {
      toast.error('Please complete required fields before proceeding');
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-red-900 mb-2">Valuation Excel File (Required)</h3>
        <p className="text-sm text-red-700 mb-2">
          <strong>Critical:</strong> Upload your most recent valuation Excel file with data updated for this quarter. This file is essential for generating an accurate valuation report.
        </p>
        <p className="text-xs text-red-600 mb-4">
          Ensure the file contains updated data for quarter ending {month} {year} before uploading.
        </p>
        <div className="border-2 border-dashed border-red-300 rounded-lg p-6 text-center bg-white">
          <input
            type="file"
            id="valuationExcel"
            accept=".xlsx,.xls,.csv"
            onChange={(e) => handleValuationExcelUpload(e.target.files[0])}
            className="hidden"
            data-testid="valuation-excel-input"
          />
          <label htmlFor="valuationExcel" className="cursor-pointer">
            {valuationExcelFile ? (
              <div className="flex flex-col items-center gap-2">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
                <span className="text-sm font-semibold text-green-700">{valuationExcelFile.name}</span>
                {valuationExcelExtracted && (
                  <span className="text-xs text-green-600">Data extracted successfully</span>
                )}
                {extracting && (
                  <div className="flex items-center gap-2 text-sm text-blue-600">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Extracting data from Excel...</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Upload className="w-8 h-8 text-red-500" />
                <span className="text-sm font-semibold text-slate-700">Click to upload Excel file (.xlsx, .xls, .csv)</span>
                <span className="text-xs text-slate-500">Must contain: Valuation History, LRE Scenarios, DCF, Comps, Recent Trading, etc.</span>
              </div>
            )}
          </label>
        </div>
      </div>

      {/* Auto-selected Previous Reports */}
      <div className="bg-slate-50 border rounded-lg p-4">
        <h3 className="font-semibold text-slate-800 mb-2">Previous Reports (Auto-Selected)</h3>
        <p className="text-xs text-slate-500 mb-3">
          The system automatically selects the 2 most recent reports before Q{quarter} {year} from your library to use as context and form a narrative on how Fresh Clinics has been tracking. Manage uploaded reports in the "Manage Report" section.
        </p>
        {autoSelectLoading ? (
          <div className="flex items-center gap-2 text-sm text-blue-600 py-2" data-testid="auto-select-loading">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Finding previous reports...</span>
          </div>
        ) : autoSelectedReports.length > 0 ? (
          <div className="space-y-2" data-testid="auto-selected-reports">
            {autoSelectedReports.map((report, idx) => (
              <div key={report.id} className="flex items-center gap-2 p-2 bg-white rounded border">
                <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                <div>
                  <span className="text-sm font-medium">{report.filename}</span>
                  <span className="text-xs text-slate-400 ml-2">
                    ({['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][parseInt(report.month)-1] || report.month} {report.year})
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-amber-600 py-2" data-testid="no-previous-reports">
            No previous reports found. Upload reports in "Manage Report" &rarr; "Previous Reports" tab.
          </p>
        )}
      </div>
    </div>
  );


  const renderStep2ValuationReview = () => {
    // Show a combined interface with both Excel-extracted data and manual entry fields
    const hasExcelData = valuationExcelData && Object.keys(valuationExcelData).length > 0;
    
    const getExcelFieldStatus = (fieldName) => {
      if (!hasExcelData) return 'empty';
      const statusKey = `${fieldName}_status`;
      return valuationExcelData[statusKey] || 'empty';
    };

    const getExcelFieldValue = (fieldName) => {
      if (!hasExcelData) return '';
      return valuationExcelData[fieldName] || '';
    };
    
    return (
      <div className="space-y-6">
        {hasExcelData && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <h3 className="font-semibold text-green-900">Data Extracted from Valuation Excel</h3>
            </div>
            <p className="text-sm text-green-700 mt-2">
              Excel data has been pre-filled below. Review and correct any values as needed.
            </p>
          </div>
        )}
        
        {!hasExcelData && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-slate-700">
              No valuation Excel file was uploaded. Please enter the data manually below, or go back to Step 1 to upload an Excel file for automatic extraction.
            </p>
          </div>
        )}

        {/* Section A: Executive Summary & Dashboard (Excel Extracted) */}
        <div className="space-y-4 p-6 bg-slate-50 rounded-lg">
          <h3 className="font-bold text-lg mb-4 text-slate-900">A. Executive Summary & Dashboard</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Share Price (A$)</Label>
              <Input
                type="text"
                value={getExcelFieldValue('share_price')}
                onChange={(e) => updateValuationField('share_price', e.target.value)}
                className={getStatusColor(getExcelFieldStatus('share_price'))}
                placeholder="e.g., 1.25"
              />
            </div>
            <div>
              <Label>Share Price Uplift (%)</Label>
              <Input
                type="text"
                value={getExcelFieldValue('share_price_uplift')}
                onChange={(e) => updateValuationField('share_price_uplift', e.target.value)}
                className={getStatusColor(getExcelFieldStatus('share_price_uplift'))}
                placeholder="e.g., 15%"
              />
            </div>
            <div>
              <Label>Enterprise Value (A$m)</Label>
              <Input
                type="text"
                value={getExcelFieldValue('enterprise_value')}
                onChange={(e) => updateValuationField('enterprise_value', e.target.value)}
                className={getStatusColor(getExcelFieldStatus('enterprise_value'))}
                placeholder="e.g., 450"
              />
            </div>
            <div>
              <Label>Equity Value / Market Cap (A$m)</Label>
              <Input
                type="text"
                value={getExcelFieldValue('equity_value')}
                onChange={(e) => updateValuationField('equity_value', e.target.value)}
                className={getStatusColor(getExcelFieldStatus('equity_value'))}
                placeholder="e.g., 420"
              />
            </div>
            <div>
              <Label>Net Debt (A$m)</Label>
              <Input
                type="text"
                value={getExcelFieldValue('net_debt')}
                onChange={(e) => updateValuationField('net_debt', e.target.value)}
                className={getStatusColor(getExcelFieldStatus('net_debt'))}
                placeholder="e.g., 30"
              />
            </div>
            <div>
              <Label>Shares on Issue (#m)</Label>
              <Input
                type="text"
                value={getExcelFieldValue('shares_on_issue')}
                onChange={(e) => updateValuationField('shares_on_issue', e.target.value)}
                className={getStatusColor(getExcelFieldStatus('shares_on_issue'))}
                placeholder="e.g., 336"
              />
            </div>
            <div>
              <Label>EV/NTM Revenue Multiple</Label>
              <Input
                type="text"
                value={getExcelFieldValue('ev_ntm_revenue')}
                onChange={(e) => updateValuationField('ev_ntm_revenue', e.target.value)}
                className={getStatusColor(getExcelFieldStatus('ev_ntm_revenue'))}
                placeholder="e.g., 2.5x"
              />
            </div>
          </div>
        </div>

        <hr className="my-6 border-t-2 border-slate-200" />

        {/* Section B: Valuation Methods (Excel Extracted) */}
        <div className="space-y-4 p-6 bg-slate-50 rounded-lg">
          <h3 className="font-bold text-lg mb-4 text-slate-900">B. Valuation Methods (Football Field)</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Long Run Earnings (LRE) Range (A$m)</Label>
              <Input
                type="text"
                value={getExcelFieldValue('lre_range')}
                onChange={(e) => updateValuationField('lre_range', e.target.value)}
                className={getStatusColor(getExcelFieldStatus('lre_range'))}
                placeholder="e.g., 400-500"
              />
            </div>
            <div>
              <Label>DCF Range (Min-Max, A$m)</Label>
              <Input
                type="text"
                value={getExcelFieldValue('dcf_range')}
                onChange={(e) => updateValuationField('dcf_range', e.target.value)}
                className={getStatusColor(getExcelFieldStatus('dcf_range'))}
                placeholder="e.g., 380-460"
              />
            </div>
            <div>
              <Label>EV/LTM Revenue Range (x)</Label>
              <Input
                type="text"
                value={getExcelFieldValue('ev_ltm_revenue_range')}
                onChange={(e) => updateValuationField('ev_ltm_revenue_range', e.target.value)}
                className={getStatusColor(getExcelFieldStatus('ev_ltm_revenue_range'))}
                placeholder="e.g., 2.0x-2.8x"
              />
            </div>
            <div>
              <Label>EV/NTM Revenue Range (x)</Label>
              <Input
                type="text"
                value={getExcelFieldValue('ev_ntm_revenue_range')}
                onChange={(e) => updateValuationField('ev_ntm_revenue_range', e.target.value)}
                className={getStatusColor(getExcelFieldStatus('ev_ntm_revenue_range'))}
                placeholder="e.g., 2.2x-3.0x"
              />
            </div>
            <div>
              <Label>Sum of the Parts (SOTP) Range (A$m)</Label>
              <Input
                type="text"
                value={getExcelFieldValue('sotp_range')}
                onChange={(e) => updateValuationField('sotp_range', e.target.value)}
                className={getStatusColor(getExcelFieldStatus('sotp_range'))}
                placeholder="e.g., 420-480"
              />
            </div>
          </div>
        </div>

        <hr className="my-6 border-t-2 border-slate-200" />

        {/* Section C: Long Run Earnings (Excel Extracted) */}
        <div className="space-y-4 p-6 bg-slate-50 rounded-lg">
          <h3 className="font-bold text-lg mb-4 text-slate-900">C. Long Run Earnings (LRE) - Primary Method</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Net Revenue in 4 Years ($m)</Label>
              <Input
                type="text"
                value={getExcelFieldValue('lre_net_revenue_4y')}
                onChange={(e) => updateValuationField('lre_net_revenue_4y', e.target.value)}
                className={getStatusColor(getExcelFieldStatus('lre_net_revenue_4y'))}
                placeholder="e.g., 300"
              />
            </div>
            <div>
              <Label>Target EBITDA Margin (%)</Label>
              <Input
                type="text"
                value={getExcelFieldValue('lre_ebitda_margin')}
                onChange={(e) => updateValuationField('lre_ebitda_margin', e.target.value)}
                className={getStatusColor(getExcelFieldStatus('lre_ebitda_margin'))}
                placeholder="e.g., 25%"
              />
            </div>
            <div>
              <Label>Exit Multiple (EV/EBITDA)</Label>
              <Input
                type="text"
                value={getExcelFieldValue('lre_exit_multiple')}
                onChange={(e) => updateValuationField('lre_exit_multiple', e.target.value)}
                className={getStatusColor(getExcelFieldStatus('lre_exit_multiple'))}
                placeholder="e.g., 15.0x"
              />
            </div>
            <div>
              <Label>Discount Rate (%)</Label>
              <Input
                type="text"
                value={getExcelFieldValue('lre_discount_rate')}
                onChange={(e) => updateValuationField('lre_discount_rate', e.target.value)}
                className={getStatusColor(getExcelFieldStatus('lre_discount_rate'))}
                placeholder="e.g., 25%"
              />
            </div>
            <div>
              <Label>Implied Share Price (A$)</Label>
              <Input
                type="text"
                value={getExcelFieldValue('lre_implied_share_price')}
                onChange={(e) => updateValuationField('lre_implied_share_price', e.target.value)}
                className={getStatusColor(getExcelFieldStatus('lre_implied_share_price'))}
                placeholder="e.g., 1.30"
              />
            </div>
          </div>
        </div>

        <hr className="my-6 border-t-2 border-slate-200" />

        {/* Section D: DCF Cross-Check (Excel Extracted) */}
        <div className="space-y-4 p-6 bg-slate-50 rounded-lg">
          <h3 className="font-bold text-lg mb-4 text-slate-900">D. DCF Cross-Check</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>WACC (%)</Label>
              <Input
                type="text"
                value={getExcelFieldValue('dcf_wacc')}
                onChange={(e) => updateValuationField('dcf_wacc', e.target.value)}
                className={getStatusColor(getExcelFieldStatus('dcf_wacc'))}
                placeholder="e.g., 12.5%"
              />
            </div>
            <div>
              <Label>Terminal Growth Rate (%)</Label>
              <Input
                type="text"
                value={getExcelFieldValue('dcf_terminal_growth')}
                onChange={(e) => updateValuationField('dcf_terminal_growth', e.target.value)}
                className={getStatusColor(getExcelFieldStatus('dcf_terminal_growth'))}
                placeholder="e.g., 3.0%"
              />
            </div>
            <div>
              <Label>Terminal Value (A$m)</Label>
              <Input
                type="text"
                value={getExcelFieldValue('dcf_terminal_value')}
                onChange={(e) => updateValuationField('dcf_terminal_value', e.target.value)}
                className={getStatusColor(getExcelFieldStatus('dcf_terminal_value'))}
                placeholder="e.g., 800"
              />
            </div>
            <div>
              <Label>Enterprise Value (DCF, A$m)</Label>
              <Input
                type="text"
                value={getExcelFieldValue('dcf_enterprise_value')}
                onChange={(e) => updateValuationField('dcf_enterprise_value', e.target.value)}
                className={getStatusColor(getExcelFieldStatus('dcf_enterprise_value'))}
                placeholder="e.g., 450"
              />
            </div>
          </div>
        </div>

        <hr className="my-6 border-t-2 border-slate-200" />

        {/* Section E: Trading Multiples (Excel Extracted) */}
        <div className="space-y-4 p-6 bg-slate-50 rounded-lg">
          <h3 className="font-bold text-lg mb-4 text-slate-900">E. Trading Multiples (Comparable Companies)</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Peer Group Average Multiple (EV/Fwd Sales)</Label>
              <Input
                type="text"
                value={getExcelFieldValue('peer_avg_multiple')}
                onChange={(e) => updateValuationField('peer_avg_multiple', e.target.value)}
                className={getStatusColor(getExcelFieldStatus('peer_avg_multiple'))}
                placeholder="e.g., 4.2x"
              />
            </div>
            <div>
              <Label>Discount to Peers (%)</Label>
              <Input
                type="text"
                value={getExcelFieldValue('discount_to_peers')}
                onChange={(e) => updateValuationField('discount_to_peers', e.target.value)}
                className={getStatusColor(getExcelFieldStatus('discount_to_peers'))}
                placeholder="e.g., -35%"
              />
            </div>
            <div className="col-span-2">
              <Label>Peer List (Company Names)</Label>
              <Textarea
                value={getExcelFieldValue('peer_list')}
                onChange={(e) => updateValuationField('peer_list', e.target.value)}
                className={getStatusColor(getExcelFieldStatus('peer_list'))}
                placeholder="e.g., Adobe, Salesforce, Atlassian, ..."
                rows={2}
              />
            </div>
          </div>
        </div>

        <hr className="my-6 border-t-2 border-slate-200" />

        {/* Section F: Financial Projections & Actuals (Excel Extracted) */}
        <div className="space-y-4 p-6 bg-slate-50 rounded-lg">
          <h3 className="font-bold text-lg mb-4 text-slate-900">F. Financial Projections & Actuals</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Current ARR ($m)</Label>
              <Input
                type="text"
                value={getExcelFieldValue('current_arr')}
                onChange={(e) => updateValuationField('current_arr', e.target.value)}
                className={getStatusColor(getExcelFieldStatus('current_arr'))}
                placeholder="e.g., 180"
              />
            </div>
            <div>
              <Label>Forecast ARR (CY25, $m)</Label>
              <Input
                type="text"
                value={getExcelFieldValue('forecast_arr')}
                onChange={(e) => updateValuationField('forecast_arr', e.target.value)}
                className={getStatusColor(getExcelFieldStatus('forecast_arr'))}
                placeholder="e.g., 210"
              />
            </div>
            <div>
              <Label>Cash EBITDA ($m)</Label>
              <Input
                type="text"
                value={getExcelFieldValue('cash_ebitda')}
                onChange={(e) => updateValuationField('cash_ebitda', e.target.value)}
                className={getStatusColor(getExcelFieldStatus('cash_ebitda'))}
                placeholder="e.g., 35"
              />
            </div>
            <div>
              <Label>Rule of 40 (%)</Label>
              <Input
                type="text"
                value={getExcelFieldValue('rule_of_40')}
                onChange={(e) => updateValuationField('rule_of_40', e.target.value)}
                className={getStatusColor(getExcelFieldStatus('rule_of_40'))}
                placeholder="e.g., 45%"
              />
            </div>
            <div>
              <Label>Cash Balance ($m)</Label>
              <Input
                type="text"
                value={getExcelFieldValue('cash_balance')}
                onChange={(e) => updateValuationField('cash_balance', e.target.value)}
                className={getStatusColor(getExcelFieldStatus('cash_balance'))}
                placeholder="e.g., 65"
              />
            </div>
          </div>
        </div>

        <hr className="my-6 border-t-2 border-slate-200" />

        {/* Section G: Cap Table & Shareholders (Excel Extracted) */}
        <div className="space-y-4 p-6 bg-slate-50 rounded-lg">
          <h3 className="font-bold text-lg mb-4 text-slate-900">G. Cap Table & Shareholders</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Potentia Holding (%)</Label>
              <Input
                type="text"
                value={getExcelFieldValue('potentia_holding')}
                onChange={(e) => updateValuationField('potentia_holding', e.target.value)}
                className={getStatusColor(getExcelFieldStatus('potentia_holding'))}
                placeholder="e.g., 8.5%"
              />
            </div>
            <div>
              <Label>JAADE Holding (%)</Label>
              <Input
                type="text"
                value={getExcelFieldValue('jaade_holding')}
                onChange={(e) => updateValuationField('jaade_holding', e.target.value)}
                className={getStatusColor(getExcelFieldStatus('jaade_holding'))}
                placeholder="e.g., 3.2%"
              />
            </div>
            <div>
              <Label>JAADE Carrying Value (A$m)</Label>
              <Input
                type="text"
                value={getExcelFieldValue('jaade_carrying_value')}
                onChange={(e) => updateValuationField('jaade_carrying_value', e.target.value)}
                className={getStatusColor(getExcelFieldStatus('jaade_carrying_value'))}
                placeholder="e.g., 14.5"
              />
            </div>
            <div>
              <Label>JAADE IRR (%)</Label>
              <Input
                type="text"
                value={getExcelFieldValue('jaade_irr')}
                onChange={(e) => updateValuationField('jaade_irr', e.target.value)}
                className={getStatusColor(getExcelFieldStatus('jaade_irr'))}
                placeholder="e.g., 22%"
              />
            </div>
          </div>
        </div>

        <hr className="my-6 border-t-2 border-slate-200" />

        {/* Section H: Company Data */}
        {renderSection1Fields()}
        <hr className="my-6 border-t-2 border-slate-200" />
        
        {/* Section I: Trading Multiple Evolution */}
        {renderSection2Fields()}
        <hr className="my-6 border-t-2 border-slate-200" />
        
        {/* Section J: Financial Performance & Metrics */}
        {renderSection4Fields()}
        <hr className="my-6 border-t-2 border-slate-200" />
        
        {/* Section K: Scorecard & Triangulation */}
        {renderSection5Fields()}
        <hr className="my-6 border-t-2 border-slate-200" />
        
        {/* Section L: Historical Trading Data */}
        {renderSection6Fields()}
        
        {/* Legend */}
        <div className="text-sm text-slate-600 mt-8 p-4 bg-slate-50 rounded-lg">
          <p className="font-semibold mb-2">Field Status Legend:</p>
          <div className="flex gap-6">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-50 border-2 border-green-500 rounded"></div>
              <span>Filled - Data entered or extracted</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-orange-50 border-2 border-orange-500 rounded"></div>
              <span>Ambiguous - AI uncertain, needs review</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-50 border-2 border-red-500 rounded"></div>
              <span>Missing - No data provided</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const updateValuationField = (field, value) => {
    setValuationExcelData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const renderStep3 = () => (
    <div className="space-y-4">
      <div>
        <Label className="text-base font-semibold mb-2 block">
          Additional Documents (Up to 5)
        </Label>
        <p className="text-sm text-slate-600 mb-4">
          Upload any additional documents that may help with the valuation analysis
        </p>
        <div className="border-2 border-dashed rounded-lg p-6 text-center">
          <input
            type="file"
            id="additionalDocs"
            multiple
            accept=".pdf,.png,.jpg,.jpeg,.xlsx,.docx"
            onChange={(e) => handleAdditionalDocUpload(e.target.files)}
            className="hidden"
          />
          <label htmlFor="additionalDocs" className="cursor-pointer">
            <div className="flex flex-col items-center gap-2">
              <Upload className="w-8 h-8 text-slate-400" />
              <span className="text-sm text-slate-600">Click to upload files</span>
              <span className="text-xs text-slate-500">{additionalDocs.length}/5 uploaded</span>
            </div>
          </label>
        </div>
      </div>

      {additionalDocs.length > 0 && (
        <div className="space-y-2">
          {additionalDocs.map((doc, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-slate-600" />
                <span className="text-sm">{doc.name}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setAdditionalDocs(prev => prev.filter((_, i) => i !== index))}
              >
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderSection1Fields = () => (
    <div className="space-y-4">
      <h3 className="font-bold text-lg mb-4 text-slate-900">H. Company Data</h3>

      {/* Manual input fields */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Previous Quarter Share Price 1 ($)</Label>
          <Input
            type="number"
            step="0.01"
            value={section1Data.previousQuarterSharePrice1.value}
            onChange={(e) => handleFieldChange('section1', 'previousQuarterSharePrice1', e.target.value)}
            className={getStatusColor(section1Data.previousQuarterSharePrice1.status)}
          />
        </div>
        <div>
          <Label>Previous Quarter Share Price 2 ($)</Label>
          <Input
            type="number"
            step="0.01"
            value={section1Data.previousQuarterSharePrice2.value}
            onChange={(e) => handleFieldChange('section1', 'previousQuarterSharePrice2', e.target.value)}
            className={getStatusColor(section1Data.previousQuarterSharePrice2.status)}
          />
        </div>
        <div>
          <Label>Total JAADE Shares Held</Label>
          <Input
            type="number"
            value={section1Data.totalJAADEShares.value}
            onChange={(e) => handleFieldChange('section1', 'totalJAADEShares', e.target.value)}
            className={getStatusColor(section1Data.totalJAADEShares.status)}
          />
        </div>
        <div>
          <Label>Total Shares on Issue</Label>
          <Input
            type="number"
            value={section1Data.totalSharesOnIssue.value}
            onChange={(e) => handleFieldChange('section1', 'totalSharesOnIssue', e.target.value)}
            className={getStatusColor(section1Data.totalSharesOnIssue.status)}
          />
        </div>
        <div>
          <Label>JAADE Ownership %</Label>
          <Input
            type="number"
            step="0.01"
            value={section1Data.jaadeOwnershipPercent.value}
            onChange={(e) => handleFieldChange('section1', 'jaadeOwnershipPercent', e.target.value)}
            className={getStatusColor(section1Data.jaadeOwnershipPercent.status)}
          />
        </div>
        <div>
          <Label>Current Cash Balance ($m)</Label>
          <Input
            type="number"
            step="0.1"
            value={section1Data.currentCashBalance.value}
            onChange={(e) => handleFieldChange('section1', 'currentCashBalance', e.target.value)}
            className={getStatusColor(section1Data.currentCashBalance.status)}
          />
        </div>
        <div>
          <Label>Total Debt ($m)</Label>
          <Input
            type="number"
            step="0.1"
            value={section1Data.totalDebt.value}
            onChange={(e) => handleFieldChange('section1', 'totalDebt', e.target.value)}
            className={getStatusColor(section1Data.totalDebt.status)}
          />
        </div>
        <div>
          <Label>Initial Investment Date</Label>
          <Input
            type="date"
            value={section1Data.initialInvestmentDate.value}
            onChange={(e) => handleFieldChange('section1', 'initialInvestmentDate', e.target.value)}
            className={getStatusColor(section1Data.initialInvestmentDate.status)}
          />
        </div>
        <div>
          <Label>Initial Cost ($m)</Label>
          <Input
            type="number"
            step="0.1"
            value={section1Data.initialCost.value}
            onChange={(e) => handleFieldChange('section1', 'initialCost', e.target.value)}
            className={getStatusColor(section1Data.initialCost.status)}
          />
        </div>
        <div>
          <Label>Total Capital Returned ($m)</Label>
          <Input
            type="number"
            step="0.1"
            value={section1Data.totalCapitalReturned.value}
            onChange={(e) => handleFieldChange('section1', 'totalCapitalReturned', e.target.value)}
            className={getStatusColor(section1Data.totalCapitalReturned.status)}
          />
        </div>
      </div>
    </div>
  );

  const renderSection2Fields = () => (
    <div className="space-y-4">
      <h3 className="font-bold text-lg mb-4 text-slate-900">I. Trading Multiple Evolution</h3>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Historical EV Data</Label>
          <Textarea
            value={section2Data.historicalEV.value}
            onChange={(e) => handleFieldChange('section2', 'historicalEV', e.target.value)}
            className={getStatusColor(section2Data.historicalEV.status)}
            placeholder="Quarterly historical EV values"
            rows={3}
          />
        </div>
        <div>
          <Label>Fresh Clinics NTM Revenue</Label>
          <Textarea
            value={section2Data.nitroNTMRevenue.value}
            onChange={(e) => handleFieldChange('section2', 'nitroNTMRevenue', e.target.value)}
            className={getStatusColor(section2Data.nitroNTMRevenue.status)}
            placeholder="Next-twelve-months revenue projection"
            rows={3}
          />
        </div>
        <div>
          <Label>Australian SaaS Multiples</Label>
          <Textarea
            value={section2Data.australianSaaSMultiples.value}
            onChange={(e) => handleFieldChange('section2', 'australianSaaSMultiples', e.target.value)}
            className={getStatusColor(section2Data.australianSaaSMultiples.status)}
            placeholder="Comparable Australian SaaS company multiples"
            rows={3}
          />
        </div>
        <div>
          <Label>Global Direct Comps Multiples</Label>
          <Textarea
            value={section2Data.globalDirectCompsMultiples.value}
            onChange={(e) => handleFieldChange('section2', 'globalDirectCompsMultiples', e.target.value)}
            className={getStatusColor(section2Data.globalDirectCompsMultiples.status)}
            placeholder="Global direct competitor multiples"
            rows={3}
          />
        </div>
      </div>
    </div>
  );

  const renderSection3Fields = () => (
    <div className="space-y-4">
      <h3 className="font-bold text-lg mb-4">3. Review of Operational Factors</h3>
      
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <Label className="text-sm font-semibold mb-2 block">
          Upload Documents for Auto-Extraction (Optional)
        </Label>
        <input
          type="file"
          multiple
          accept=".pdf,.png,.jpg,.jpeg"
          onChange={(e) => handleSectionDocUpload('section3', e.target.files)}
          className="text-sm"
        />
        {extracting && (
          <div className="mt-2 flex items-center gap-2 text-sm text-blue-600">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Extracting data...</span>
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div>
          <Label>Operational Milestones</Label>
          <Textarea
            value={section3Data.operationalMilestones.value}
            onChange={(e) => handleFieldChange('section3', 'operationalMilestones', e.target.value)}
            className={getStatusColor(section3Data.operationalMilestones.status)}
            placeholder="Key operational achievements and milestones this quarter"
            rows={4}
          />
        </div>
        <div>
          <Label>Strategic Initiatives</Label>
          <Textarea
            value={section3Data.strategicInitiatives.value}
            onChange={(e) => handleFieldChange('section3', 'strategicInitiatives', e.target.value)}
            className={getStatusColor(section3Data.strategicInitiatives.status)}
            placeholder="Major strategic initiatives and future plans"
            rows={4}
          />
        </div>
      </div>
    </div>
  );

  const renderSection4Fields = () => (
    <div className="space-y-4">
      <h3 className="font-bold text-lg mb-4 text-slate-900">J. Financial Performance & SaaS Metrics</h3>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>ARR ($m)</Label>
          <Input
            type="number"
            step="0.1"
            value={section4Data.arr.value}
            onChange={(e) => handleFieldChange('section4', 'arr', e.target.value)}
            className={getStatusColor(section4Data.arr.status)}
          />
        </div>
        <div>
          <Label>Total Revenue ($m)</Label>
          <Input
            type="number"
            step="0.1"
            value={section4Data.totalRevenue.value}
            onChange={(e) => handleFieldChange('section4', 'totalRevenue', e.target.value)}
            className={getStatusColor(section4Data.totalRevenue.status)}
          />
        </div>
        <div>
          <Label>YoY Growth Rate (%)</Label>
          <Input
            type="number"
            step="0.1"
            value={section4Data.yoyGrowthRate.value}
            onChange={(e) => handleFieldChange('section4', 'yoyGrowthRate', e.target.value)}
            className={getStatusColor(section4Data.yoyGrowthRate.status)}
          />
        </div>
        <div>
          <Label>Subscription Revenue ($m)</Label>
          <Input
            type="number"
            step="0.1"
            value={section4Data.subscriptionRevenue.value}
            onChange={(e) => handleFieldChange('section4', 'subscriptionRevenue', e.target.value)}
            className={getStatusColor(section4Data.subscriptionRevenue.status)}
          />
        </div>
        <div>
          <Label>Perpetual Revenue ($m)</Label>
          <Input
            type="number"
            step="0.1"
            value={section4Data.perpetualRevenue.value}
            onChange={(e) => handleFieldChange('section4', 'perpetualRevenue', e.target.value)}
            className={getStatusColor(section4Data.perpetualRevenue.status)}
          />
        </div>
        <div>
          <Label>Services Revenue ($m)</Label>
          <Input
            type="number"
            step="0.1"
            value={section4Data.servicesRevenue.value}
            onChange={(e) => handleFieldChange('section4', 'servicesRevenue', e.target.value)}
            className={getStatusColor(section4Data.servicesRevenue.status)}
          />
        </div>
        <div>
          <Label>Operating EBITDA ($m)</Label>
          <Input
            type="number"
            step="0.1"
            value={section4Data.operatingEBITDA.value}
            onChange={(e) => handleFieldChange('section4', 'operatingEBITDA', e.target.value)}
            className={getStatusColor(section4Data.operatingEBITDA.status)}
          />
        </div>
        <div>
          <Label>Cash EBITDA ($m)</Label>
          <Input
            type="number"
            step="0.1"
            value={section4Data.cashEBITDA.value}
            onChange={(e) => handleFieldChange('section4', 'cashEBITDA', e.target.value)}
            className={getStatusColor(section4Data.cashEBITDA.status)}
          />
        </div>
        <div>
          <Label>EBITDA Margin - Actual (%)</Label>
          <Input
            type="number"
            step="0.1"
            value={section4Data.ebitdaMarginActual.value}
            onChange={(e) => handleFieldChange('section4', 'ebitdaMarginActual', e.target.value)}
            className={getStatusColor(section4Data.ebitdaMarginActual.status)}
          />
        </div>
        <div>
          <Label>EBITDA Margin - Budget (%)</Label>
          <Input
            type="number"
            step="0.1"
            value={section4Data.ebitdaMarginBudget.value}
            onChange={(e) => handleFieldChange('section4', 'ebitdaMarginBudget', e.target.value)}
            className={getStatusColor(section4Data.ebitdaMarginBudget.status)}
          />
        </div>
        <div>
          <Label>Net Revenue Retention (NRR) (%)</Label>
          <Input
            type="number"
            step="0.1"
            value={section4Data.nrr.value}
            onChange={(e) => handleFieldChange('section4', 'nrr', e.target.value)}
            className={getStatusColor(section4Data.nrr.status)}
          />
        </div>
        <div>
          <Label>Gross Revenue Retention (GRR) (%)</Label>
          <Input
            type="number"
            step="0.1"
            value={section4Data.grr.value}
            onChange={(e) => handleFieldChange('section4', 'grr', e.target.value)}
            className={getStatusColor(section4Data.grr.status)}
          />
        </div>
        <div>
          <Label>Rule of 40</Label>
          <Input
            type="number"
            step="0.1"
            value={section4Data.ruleOf40.value}
            onChange={(e) => handleFieldChange('section4', 'ruleOf40', e.target.value)}
            className={getStatusColor(section4Data.ruleOf40.status)}
          />
        </div>
      </div>
    </div>
  );

  const renderSection5Fields = () => (
    <div className="space-y-4">
      <h3 className="font-bold text-lg mb-4 text-slate-900">K. Scorecard & Triangulation</h3>

      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label>Peer Benchmarks</Label>
          <Textarea
            value={section5Data.peerBenchmarks.value}
            onChange={(e) => handleFieldChange('section5', 'peerBenchmarks', e.target.value)}
            className={getStatusColor(section5Data.peerBenchmarks.status)}
            placeholder="Peer company comparisons and benchmark data"
            rows={3}
          />
        </div>
        
        <div className="col-span-2">
          <h4 className="font-semibold text-md mb-3 mt-4">DCF Parameters</h4>
        </div>
        <div>
          <Label>WACC (%)</Label>
          <Input
            type="number"
            step="0.1"
            value={section5Data.dcfWACC.value}
            onChange={(e) => handleFieldChange('section5', 'dcfWACC', e.target.value)}
            className={getStatusColor(section5Data.dcfWACC.status)}
          />
        </div>
        <div>
          <Label>Terminal Growth (%)</Label>
          <Input
            type="number"
            step="0.1"
            value={section5Data.dcfTerminalGrowth.value}
            onChange={(e) => handleFieldChange('section5', 'dcfTerminalGrowth', e.target.value)}
            className={getStatusColor(section5Data.dcfTerminalGrowth.status)}
          />
        </div>
        <div>
          <Label>Exit Multiple Range</Label>
          <Input
            type="text"
            value={section5Data.dcfExitMultiple.value}
            onChange={(e) => handleFieldChange('section5', 'dcfExitMultiple', e.target.value)}
            className={getStatusColor(section5Data.dcfExitMultiple.status)}
            placeholder="e.g., 10-12.5"
          />
        </div>
        
        <div className="col-span-2">
          <h4 className="font-semibold text-md mb-3 mt-4">Long Run Earnings (LRE) Parameters</h4>
        </div>
        <div>
          <Label>Forecasted EBITDA (4 years, $m)</Label>
          <Input
            type="number"
            step="0.1"
            value={section5Data.lreForecastedEBITDA.value}
            onChange={(e) => handleFieldChange('section5', 'lreForecastedEBITDA', e.target.value)}
            className={getStatusColor(section5Data.lreForecastedEBITDA.status)}
          />
        </div>
        <div>
          <Label>LRE Exit Multiple (x)</Label>
          <Input
            type="number"
            step="0.1"
            value={section5Data.lreExitMultiple.value}
            onChange={(e) => handleFieldChange('section5', 'lreExitMultiple', e.target.value)}
            className={getStatusColor(section5Data.lreExitMultiple.status)}
          />
        </div>
        <div>
          <Label>Liquidity Discount (%)</Label>
          <Input
            type="number"
            step="0.1"
            value={section5Data.lreLiquidityDiscount.value}
            onChange={(e) => handleFieldChange('section5', 'lreLiquidityDiscount', e.target.value)}
            className={getStatusColor(section5Data.lreLiquidityDiscount.status)}
          />
        </div>
        <div>
          <Label>Control Premium (%)</Label>
          <Input
            type="number"
            step="0.1"
            value={section5Data.lreControlPremium.value}
            onChange={(e) => handleFieldChange('section5', 'lreControlPremium', e.target.value)}
            className={getStatusColor(section5Data.lreControlPremium.status)}
          />
        </div>
        <div>
          <Label>Discount Rate (%)</Label>
          <Input
            type="number"
            step="0.1"
            value={section5Data.lreDiscountRate.value}
            onChange={(e) => handleFieldChange('section5', 'lreDiscountRate', e.target.value)}
            className={getStatusColor(section5Data.lreDiscountRate.status)}
          />
        </div>
      </div>
    </div>
  );

  const renderSection6Fields = () => (
    <div className="space-y-4">
      <h3 className="font-bold text-lg mb-4 text-slate-900">L. Historical Trading Data - 10 Quarters</h3>

      <div className="space-y-6">
        <p className="text-sm font-semibold text-slate-700">
          Enter data for 10 quarters (Date, EV, Fresh Clinics Multiple, Comps Multiple):
        </p>
        
        {section6Data.quarters.map((quarter, idx) => {
          const quarterStatus = quarter.date && quarter.ev && quarter.nitroMultiple && quarter.compsMultiple ? 'filled' : 
                               (quarter.date || quarter.ev || quarter.nitroMultiple || quarter.compsMultiple) ? 'ambiguous' : 'empty';
          const statusColor = getStatusColor(quarterStatus);
          
          return (
            <div key={idx} className={`grid grid-cols-4 gap-3 p-3 rounded border-2 ${statusColor}`}>
              <div>
                <Label className="text-xs">Q{idx + 1} Date</Label>
                <Input
                  type="text"
                  placeholder="Apr-23"
                  value={quarter.date}
                  onChange={(e) => {
                    const newQuarters = [...section6Data.quarters];
                    newQuarters[idx] = { ...newQuarters[idx], date: e.target.value };
                    setSection6Data({ quarters: newQuarters });
                  }}
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">EV (A$m)</Label>
                <Input
                  type="number"
                  step="0.1"
                  placeholder="545.0"
                  value={quarter.ev}
                  onChange={(e) => {
                    const newQuarters = [...section6Data.quarters];
                    newQuarters[idx] = { ...newQuarters[idx], ev: e.target.value };
                    setSection6Data({ quarters: newQuarters });
                  }}
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Fresh Clinics Multiple</Label>
                <Input
                  type="number"
                  step="0.1"
                  placeholder="4.8"
                  value={quarter.nitroMultiple}
                  onChange={(e) => {
                    const newQuarters = [...section6Data.quarters];
                    newQuarters[idx] = { ...newQuarters[idx], nitroMultiple: e.target.value };
                    setSection6Data({ quarters: newQuarters });
                  }}
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Comps Multiple</Label>
                <Input
                  type="number"
                  step="0.1"
                  placeholder="4.6"
                  value={quarter.compsMultiple}
                  onChange={(e) => {
                    const newQuarters = [...section6Data.quarters];
                    newQuarters[idx] = { ...newQuarters[idx], compsMultiple: e.target.value };
                    setSection6Data({ quarters: newQuarters });
                  }}
                  className="h-8 text-sm"
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const handleGenerateSummaries = async () => {
    setSummariesLoading(true);
    toast.info('Generating AI summaries with document context... This may take 1-2 minutes.');

    try {
      const formData = new FormData();
      formData.append('quarter', quarter);
      formData.append('year', year);
      formData.append('section1_data', JSON.stringify(section1Data));
      formData.append('section2_data', JSON.stringify(section2Data));
      formData.append('section4_data', JSON.stringify(section4Data));
      formData.append('section5_data', JSON.stringify(section5Data));
      if (valuationExcelData) {
        formData.append('valuation_excel_data', JSON.stringify(valuationExcelData));
      }
      // Pass auto-selected previous report IDs
      if (autoSelectedReports.length > 0) {
        formData.append('previous_report_ids', autoSelectedReports.map(r => r.id).join(','));
      }
      // Pass additional docs (PDFs)
      additionalDocs.forEach((doc, index) => {
        formData.append(`additional_doc_${index}`, doc);
      });

      // Pass qualitative story updates if provided
      if (qualitativeStoryUpdates.trim()) {
        formData.append('qualitative_story_updates', qualitativeStoryUpdates);
      }

      const submitRes = await axios.post(`${API}/reports/fresh-clinics-v1/generate-summaries`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000
      });

      toast.info('Summaries processing with document analysis...');
      const job = await pollJobStatus(`${API}/reports/fresh-clinics-v1/generate-summaries/${submitRes.data.job_id}/status`);
      const result = job.result;

      if (result.success) {
        setAiSummaries({
          strategicInitiatives: result.strategic_initiatives,
          operationalMilestones: result.operational_milestones,
          overallUpdate: result.overall_update
        });
        setSummariesGenerated(true);
        toast.success('AI summaries generated successfully!');
      } else {
        toast.error('Failed to generate summaries.');
      }
    } catch (error) {
      console.error('Summary generation error:', error);
      toast.error(error.message || 'Failed to generate AI summaries. Please try again.');
    } finally {
      setSummariesLoading(false);
    }
  };

  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-blue-900" data-testid="step4-header">AI-Generated Summaries</h3>
        <p className="text-sm text-blue-700 mt-1">
          Generate AI-powered summaries based on all extracted financial data. Review the outputs and add your own comments below each section.
        </p>
      </div>

      {!summariesGenerated && !summariesLoading && (
        <div className="space-y-6">
          {/* Qualitative story updates input - shown before generating */}
          <div className="border border-slate-200 rounded-lg p-4">
            <label className="block text-sm font-semibold text-slate-800 mb-1">
              Additional Qualitative Story Updates <span className="text-slate-400 font-normal">(Optional)</span>
            </label>
            <p className="text-xs text-slate-500 mb-2">
              Provide any additional qualitative context, market developments, or narrative updates to incorporate into all report sections.
            </p>
            <textarea
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              rows={4}
              placeholder="e.g. Management announced a new product launch in Q4, strong competitive positioning vs Adobe, key customer wins in the enterprise segment..."
              value={qualitativeStoryUpdates}
              onChange={e => setQualitativeStoryUpdates(e.target.value)}
            />
          </div>

          <div className="text-center py-6" data-testid="generate-summaries-section">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
              <FileText className="w-8 h-8 text-blue-600" />
            </div>
            <h4 className="text-lg font-semibold mb-2">Ready to Generate Summaries</h4>
            <p className="text-sm text-slate-500 mb-6 max-w-md mx-auto">
              The AI will analyze all extracted financial data and generate summaries for strategic initiatives, operational milestones, and an overall valuation update.
            </p>
            <Button
              onClick={handleGenerateSummaries}
              data-testid="generate-summaries-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2"
            >
              Generate Summaries
            </Button>
          </div>
        </div>
      )}

      {summariesLoading && (
        <div className="text-center py-8" data-testid="summaries-loading">
          <Loader2 className="w-10 h-10 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-sm text-slate-600">Generating summaries with AI... This may take 1-2 minutes.</p>
        </div>
      )}

      {summariesGenerated && (
        <div className="space-y-8" data-testid="summaries-results">
          {/* Qualitative story updates - always visible after summaries generated */}
          <div className="border border-amber-200 bg-amber-50 rounded-lg p-4">
            <label className="block text-sm font-semibold text-slate-800 mb-1">
              Additional Qualitative Story Updates <span className="text-slate-400 font-normal">(Optional)</span>
            </label>
            <p className="text-xs text-slate-500 mb-2">
              This text will be passed to all AI section-generation calls in the final report.
            </p>
            <textarea
              className="w-full border border-amber-300 rounded-md px-3 py-2 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
              rows={3}
              placeholder="e.g. Management announced a new product launch in Q4, strong competitive positioning vs Adobe..."
              value={qualitativeStoryUpdates}
              onChange={e => setQualitativeStoryUpdates(e.target.value)}
            />
          </div>

          {/* Strategic Initiatives */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
              <h4 className="font-semibold text-slate-800">Strategic Initiatives This Quarter</h4>
            </div>
            <div className="p-4">
              <div
                className="bg-slate-50 rounded-md p-4 text-sm text-slate-700 whitespace-pre-wrap mb-4 border border-slate-100"
                data-testid="strategic-initiatives-output"
              >
                {aiSummaries.strategicInitiatives}
              </div>
              <Label className="text-sm font-medium mb-1 block">Your Comments</Label>
              <Textarea
                value={userComments.strategicInitiatives}
                onChange={(e) => setUserComments(prev => ({ ...prev, strategicInitiatives: e.target.value }))}
                placeholder="Add your own comments or context about strategic initiatives..."
                rows={3}
                data-testid="strategic-initiatives-comment"
              />
            </div>
          </div>

          {/* Operational Milestones */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
              <h4 className="font-semibold text-slate-800">Operational Milestones This Quarter</h4>
            </div>
            <div className="p-4">
              <div
                className="bg-slate-50 rounded-md p-4 text-sm text-slate-700 whitespace-pre-wrap mb-4 border border-slate-100"
                data-testid="operational-milestones-output"
              >
                {aiSummaries.operationalMilestones}
              </div>
              <Label className="text-sm font-medium mb-1 block">Your Comments</Label>
              <Textarea
                value={userComments.operationalMilestones}
                onChange={(e) => setUserComments(prev => ({ ...prev, operationalMilestones: e.target.value }))}
                placeholder="Add your own comments or context about operational milestones..."
                rows={3}
                data-testid="operational-milestones-comment"
              />
            </div>
          </div>

          {/* Overall Valuation Update */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
              <h4 className="font-semibold text-slate-800">Overall Valuation Update</h4>
            </div>
            <div className="p-4">
              <div
                className="bg-slate-50 rounded-md p-4 text-sm text-slate-700 whitespace-pre-wrap mb-4 border border-slate-100"
                data-testid="overall-update-output"
              >
                {aiSummaries.overallUpdate}
              </div>
              <Label className="text-sm font-medium mb-1 block">Your Comments</Label>
              <Textarea
                value={userComments.overallUpdate}
                onChange={(e) => setUserComments(prev => ({ ...prev, overallUpdate: e.target.value }))}
                placeholder="Add your own comments or qualitative updates..."
                rows={3}
                data-testid="overall-update-comment"
              />
            </div>
          </div>

          <div className="flex justify-center">
            <Button
              variant="outline"
              onClick={() => {
                setSummariesGenerated(false);
                setAiSummaries({ strategicInitiatives: '', operationalMilestones: '', overallUpdate: '' });
              }}
              data-testid="regenerate-summaries-btn"
              className="text-sm"
            >
              Regenerate Summaries
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  // Step 5 no longer auto-triggers — user must upload financials screenshot and click Generate

  // Helper to poll a background job until complete
  const pollJobStatus = async (url, intervalMs = 5000, maxMs = 600000) => {
    const start = Date.now();
    while (Date.now() - start < maxMs) {
      await new Promise(r => setTimeout(r, intervalMs));
      const res = await axios.get(url);
      const data = res.data;
      if (data.status === 'complete') return data;
      if (data.status === 'error') throw new Error(data.error || 'Job failed');
    }
    throw new Error('Job timed out');
  };

  const handleFullReportGeneration = async () => {
    setReportGenerating(true);
    setLoading(true);
    toast.info('Generating report... This may take 2-3 minutes.');

    try {
      // Step A: Run AI Valuation Analysis (background job)
      const analysisFormData = new FormData();
      if (autoSelectedReports.length > 0) {
        analysisFormData.append('previous_report_ids', autoSelectedReports.map(r => r.id).join(','));
      }
      additionalDocs.forEach((doc, index) => {
        analysisFormData.append(`additional_doc_${index}`, doc);
      });
      analysisFormData.append('quarter', quarter);
      analysisFormData.append('year', year);
      analysisFormData.append('valuation_guidance', '');
      analysisFormData.append('section1_data', JSON.stringify(section1Data));
      analysisFormData.append('section2_data', JSON.stringify(section2Data));
      const enrichedSection3 = {
        ...section3Data,
        operationalMilestones: { value: aiSummaries.operationalMilestones + (userComments.operationalMilestones ? '\n\nUser Comments:\n' + userComments.operationalMilestones : ''), status: 'filled' },
        strategicInitiatives: { value: aiSummaries.strategicInitiatives + (userComments.strategicInitiatives ? '\n\nUser Comments:\n' + userComments.strategicInitiatives : ''), status: 'filled' },
        overallUpdate: { value: aiSummaries.overallUpdate + (userComments.overallUpdate ? '\n\nUser Comments:\n' + userComments.overallUpdate : ''), status: 'filled' }
      };
      analysisFormData.append('section3_data', JSON.stringify(enrichedSection3));
      analysisFormData.append('section4_data', JSON.stringify(section4Data));
      analysisFormData.append('section5_data', JSON.stringify(section5Data));
      if (valuationExcelData) {
        analysisFormData.append('valuation_excel_data', JSON.stringify(valuationExcelData));
      }
      if (qualitativeStoryUpdates.trim()) {
        analysisFormData.append('qualitative_story_updates', qualitativeStoryUpdates);
      }

      const analyzeRes = await axios.post(`${API}/reports/fresh-clinics-v1/analyze`, analysisFormData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000
      });

      toast.info('AI analysis started. Polling for results...');
      const analyzeJob = await pollJobStatus(`${API}/reports/fresh-clinics-v1/analyze/${analyzeRes.data.job_id}/status`);
      const analysisResult = analyzeJob.result;

      if (analysisResult.needs_more_data) {
        setAiNeedsMoreData(true);
        setAiDataRequest(analysisResult.data_request);
        toast.warning('AI needs additional data to complete the analysis');
        setReportGenerating(false);
        setLoading(false);
        return;
      }

      const valuation = analysisResult.valuation;
      setAiValuation(valuation);
      setAiAnalysisComplete(true);

      // Step B: Generate Final Report (background job)
      const reportFormData = new FormData();
      reportFormData.append('quarter', quarter);
      reportFormData.append('year', year);
      reportFormData.append('valuation_data', JSON.stringify(valuation));
      reportFormData.append('user_commentary', '');
      reportFormData.append('section1_data', JSON.stringify(section1Data));
      reportFormData.append('section2_data', JSON.stringify(section2Data));
      reportFormData.append('section3_data', JSON.stringify(enrichedSection3));
      reportFormData.append('section4_data', JSON.stringify(section4Data));
      reportFormData.append('section5_data', JSON.stringify(section5Data));
      reportFormData.append('valuation_guidance', '');
      if (valuationExcelData) {
        reportFormData.append('valuation_excel_data', JSON.stringify(valuationExcelData));
      }
      if (valuationExcelFile) {
        reportFormData.append('valuation_excel_file', valuationExcelFile);
      }
      if (financialsScreenshot) {
        reportFormData.append('financials_screenshot', financialsScreenshot);
      }
      if (keyFinancialsScreenshot) {
        reportFormData.append('key_financials_screenshot', keyFinancialsScreenshot);
      }
      if (qualitativeStoryUpdates.trim()) {
        reportFormData.append('qualitative_story_updates', qualitativeStoryUpdates);
      }

      const generateRes = await axios.post(`${API}/reports/fresh-clinics-v1/generate`, reportFormData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000
      });

      toast.info('Report generation started. Polling for results...');
      const generateJob = await pollJobStatus(`${API}/reports/fresh-clinics-v1/generate/${generateRes.data.job_id}/status`);

      setReportGenerated(true);
      toast.success('Report generated successfully!');

      if (onSubmit) {
        onSubmit(generateJob.result.report_id);
      }
    } catch (error) {
      console.error('Report generation error:', error);
      toast.error(error.message || 'Failed to generate report. Please try again.');
      reportTriggeredRef.current = false;
    } finally {
      setReportGenerating(false);
      setLoading(false);
    }
  };

  const handleProvideAdditionalData = async () => {
    setAiNeedsMoreData(false);
    setReportGenerating(true);
    setLoading(true);
    toast.info('Re-analyzing with additional data...');

    try {
      const analysisFormData = new FormData();
      if (autoSelectedReports.length > 0) {
        analysisFormData.append('previous_report_ids', autoSelectedReports.map(r => r.id).join(','));
      }
      additionalDocs.forEach((doc, index) => {
        analysisFormData.append(`additional_doc_${index}`, doc);
      });
      analysisFormData.append('quarter', quarter);
      analysisFormData.append('year', year);
      analysisFormData.append('valuation_guidance', '');
      analysisFormData.append('section1_data', JSON.stringify(section1Data));
      analysisFormData.append('section2_data', JSON.stringify(section2Data));
      const enrichedSection3 = {
        ...section3Data,
        operationalMilestones: { value: aiSummaries.operationalMilestones + (userComments.operationalMilestones ? '\n\nUser Comments:\n' + userComments.operationalMilestones : ''), status: 'filled' },
        strategicInitiatives: { value: aiSummaries.strategicInitiatives + (userComments.strategicInitiatives ? '\n\nUser Comments:\n' + userComments.strategicInitiatives : ''), status: 'filled' },
        overallUpdate: { value: aiSummaries.overallUpdate + (userComments.overallUpdate ? '\n\nUser Comments:\n' + userComments.overallUpdate : ''), status: 'filled' }
      };
      analysisFormData.append('section3_data', JSON.stringify(enrichedSection3));
      analysisFormData.append('section4_data', JSON.stringify(section4Data));
      analysisFormData.append('section5_data', JSON.stringify(section5Data));
      const textToSend = additionalDataText || 'Use market averages and reasonable SaaS benchmarks for any missing data. Proceed with the analysis.';
      analysisFormData.append('additional_data_text', textToSend);
      additionalDataFiles.forEach((file, index) => {
        analysisFormData.append(`additional_file_${index}`, file);
      });
      if (valuationExcelData) {
        analysisFormData.append('valuation_excel_data', JSON.stringify(valuationExcelData));
      }
      if (qualitativeStoryUpdates.trim()) {
        analysisFormData.append('qualitative_story_updates', qualitativeStoryUpdates);
      }

      const analyzeRes = await axios.post(`${API}/reports/fresh-clinics-v1/analyze`, analysisFormData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000
      });

      const analyzeJob = await pollJobStatus(`${API}/reports/fresh-clinics-v1/analyze/${analyzeRes.data.job_id}/status`);
      const analysisResult = analyzeJob.result;
      if (analysisResult.needs_more_data) {
        setAiNeedsMoreData(true);
        setAiDataRequest(analysisResult.data_request);
        toast.warning('AI still needs additional data');
        setReportGenerating(false);
        setLoading(false);
        return;
      }

      const valuation = analysisResult.valuation;
      setAiValuation(valuation);
      setAiAnalysisComplete(true);

      // Auto-generate final report
      const reportFormData = new FormData();
      reportFormData.append('quarter', quarter);
      reportFormData.append('year', year);
      reportFormData.append('valuation_data', JSON.stringify(valuation));
      reportFormData.append('user_commentary', '');
      reportFormData.append('section1_data', JSON.stringify(section1Data));
      reportFormData.append('section2_data', JSON.stringify(section2Data));
      reportFormData.append('section3_data', JSON.stringify(enrichedSection3));
      reportFormData.append('section4_data', JSON.stringify(section4Data));
      reportFormData.append('section5_data', JSON.stringify(section5Data));
      reportFormData.append('valuation_guidance', '');
      if (valuationExcelData) {
        reportFormData.append('valuation_excel_data', JSON.stringify(valuationExcelData));
      }
      if (valuationExcelFile) {
        reportFormData.append('valuation_excel_file', valuationExcelFile);
      }
      if (qualitativeStoryUpdates.trim()) {
        reportFormData.append('qualitative_story_updates', qualitativeStoryUpdates);
      }

      const generateRes = await axios.post(`${API}/reports/fresh-clinics-v1/generate`, reportFormData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000
      });

      const generateJob = await pollJobStatus(`${API}/reports/fresh-clinics-v1/generate/${generateRes.data.job_id}/status`);

      setReportGenerated(true);
      toast.success('Report generated successfully!');
      if (onSubmit) {
        onSubmit(generateJob.result.report_id);
      }
    } catch (error) {
      console.error('Report generation error:', error);
      toast.error(error.message || 'Failed to generate report. Please try again.');
    } finally {
      setReportGenerating(false);
      setLoading(false);
    }
  };

  const renderStep5 = () => {
    if (aiNeedsMoreData) {
      return (
        <div className="space-y-6">
          <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="font-bold text-lg mb-2">Additional Data Requested</h3>
                <p className="text-slate-700 whitespace-pre-line mb-4">{aiDataRequest}</p>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <Label className="text-base font-semibold mb-2 block">
                Provide Additional Information (Optional)
              </Label>
              <Textarea
                data-testid="additional-data-text"
                value={additionalDataText}
                onChange={(e) => setAdditionalDataText(e.target.value)}
                placeholder="Enter the requested data, or click 'Proceed with Available Data' to use market averages..."
                rows={6}
                className="w-full"
              />
            </div>
            <div>
              <Label className="text-base font-semibold mb-2 block">
                Or Upload Supporting Documents
              </Label>
              <input
                type="file"
                multiple
                accept=".pdf,.png,.jpg,.jpeg,.xlsx"
                onChange={(e) => setAdditionalDataFiles(Array.from(e.target.files))}
                className="text-sm"
              />
            </div>
            <div className="flex gap-3">
              <Button
                data-testid="submit-additional-data-btn"
                onClick={handleProvideAdditionalData}
                disabled={loading}
                className="flex-1"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Re-analyzing...
                  </>
                ) : (
                  'Submit Additional Data'
                )}
              </Button>
              <Button
                data-testid="proceed-with-defaults-btn"
                variant="outline"
                onClick={handleProvideAdditionalData}
                disabled={loading}
                className="flex-1"
              >
                Proceed with Available Data
              </Button>
            </div>
          </div>
        </div>
      );
    }

    if (reportGenerated && aiValuation) {
      return (
        <div className="space-y-6" data-testid="report-complete">
          <div className="bg-green-50 border-2 border-green-300 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="font-bold text-lg mb-2">Report Generated Successfully</h3>
              </div>
            </div>
          </div>

          <Card>
            <CardHeader style={{ backgroundColor: '#A8A67A' }}>
              <CardTitle className="text-white">Valuation Summary</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-4">
                {aiValuation.fair_value && (
                  <div className="border-b pb-4">
                    <Label className="font-semibold text-lg">Fair Value Range</Label>
                    <p className="text-2xl font-bold text-green-600 mt-2">{aiValuation.fair_value}</p>
                  </div>
                )}
                {aiValuation.methodology && (
                  <div className="border-b pb-4">
                    <Label className="font-semibold">Primary Methodology</Label>
                    <p className="mt-2 text-slate-700">{extractText(aiValuation.methodology)}</p>
                  </div>
                )}
                {aiValuation.lre_analysis && (
                  <div className="border-b pb-4">
                    <Label className="font-semibold">Long Run Earnings (LRE) Analysis</Label>
                    <p className="mt-2 text-slate-700 whitespace-pre-line">{extractText(aiValuation.lre_analysis)}</p>
                  </div>
                )}
                {aiValuation.triangulation && (
                  <div className="border-b pb-4">
                    <Label className="font-semibold">Triangulation Methods</Label>
                    <div className="mt-2 space-y-2">
                      {Object.entries(aiValuation.triangulation).map(([method, value]) => (
                        <div key={method} className="flex justify-between items-center">
                          <span className="text-slate-600 capitalize">{method.replace(/_/g, ' ')}:</span>
                          <span className="font-semibold">{typeof value === 'object' ? extractText(value) : value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {aiValuation.qualitative_factors && (
                  <div className="border-b pb-4">
                    <Label className="font-semibold">Qualitative Assessment</Label>
                    <p className="mt-2 text-slate-700 whitespace-pre-line">{extractText(aiValuation.qualitative_factors)}</p>
                  </div>
                )}
                {aiValuation.recommendation && (
                  <div className="bg-slate-100 p-4 rounded-lg">
                    <Label className="font-semibold">Recommendation</Label>
                    <p className="mt-2 text-slate-700 whitespace-pre-line">{extractText(aiValuation.recommendation)}</p>
                  </div>
                )}
                {aiValuation.reasoning && (
                  <div>
                    <Label className="font-semibold">Detailed Reasoning</Label>
                    <p className="mt-2 text-slate-700 whitespace-pre-line">{extractText(aiValuation.reasoning)}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Default: show financials screenshot upload + Generate button
    return (
      <div className="space-y-6" data-testid="step5-generate">
        {/* Optional: Financials Screenshot Upload */}
        <div className={`rounded-lg border-2 p-6 ${financialsScreenshot ? 'border-green-400 bg-green-50' : 'border-slate-300 bg-white'}`}>
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${financialsScreenshot ? 'bg-green-600' : 'bg-slate-400'}`}>
              {financialsScreenshot ? <CheckCircle2 className="w-4 h-4 text-white" /> : <span className="text-white text-xs font-bold">?</span>}
            </div>
            <div>
              <h3 className="font-bold text-base">Financial Performance Screenshot <span className="text-slate-500 font-normal text-sm">(Optional)</span></h3>
              <p className="text-sm text-slate-600">Optionally upload an image screenshot of the company's financial projections table. If provided, it will be embedded in the report. If you have uploaded a financials file in Step 3, AI will use that data instead.</p>
            </div>
          </div>

          {financialsScreenshot ? (
            <div className="flex items-center gap-3 mt-2">
              <div className="flex-1 bg-green-100 rounded px-3 py-2 text-sm text-green-800 font-medium truncate">
                ✓ {financialsScreenshot.name}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFinancialsScreenshot(null)}
              >
                Remove
              </Button>
            </div>
          ) : (
            <label className="block mt-2 cursor-pointer">
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-slate-400 transition-colors">
                <p className="text-sm text-slate-600 font-medium">Click to upload or drag & drop</p>
                <p className="text-xs text-slate-500 mt-1">PNG, JPG, JPEG accepted</p>
              </div>
              <input
                type="file"
                accept=".png,.jpg,.jpeg"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files[0]) setFinancialsScreenshot(e.target.files[0]);
                }}
              />
            </label>
          )}
        </div>

        {/* Optional: Key Financials & Valuation Multiples Screenshot */}
        <div className={`rounded-lg border-2 p-6 ${keyFinancialsScreenshot ? 'border-green-400 bg-green-50' : 'border-slate-300 bg-white'}`}>
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${keyFinancialsScreenshot ? 'bg-green-600' : 'bg-slate-400'}`}>
              {keyFinancialsScreenshot ? <CheckCircle2 className="w-4 h-4 text-white" /> : <span className="text-white text-xs font-bold">?</span>}
            </div>
            <div>
              <h3 className="font-bold text-base">Key Financials &amp; Valuation Multiples Screenshot <span className="text-slate-500 font-normal text-sm">(Optional)</span></h3>
              <p className="text-sm text-slate-600">Upload a screenshot of the Key Financials &amp; Valuation Multiples table. It will be embedded directly into the report in place of a generated table.</p>
            </div>
          </div>

          {keyFinancialsScreenshot ? (
            <div className="flex items-center gap-3 mt-2">
              <div className="flex-1 bg-green-100 rounded px-3 py-2 text-sm text-green-800 font-medium truncate">
                ✓ {keyFinancialsScreenshot.name}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setKeyFinancialsScreenshot(null)}
              >
                Remove
              </Button>
            </div>
          ) : (
            <label className="block mt-2 cursor-pointer">
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-slate-400 transition-colors">
                <p className="text-sm text-slate-600 font-medium">Click to upload or drag & drop</p>
                <p className="text-xs text-slate-500 mt-1">PNG, JPG, JPEG accepted</p>
              </div>
              <input
                type="file"
                accept=".png,.jpg,.jpeg"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files[0]) setKeyFinancialsScreenshot(e.target.files[0]);
                }}
              />
            </label>
          )}
        </div>

        {/* Generate button */}
        <div className="rounded-lg border-2 p-6">
          <h3 className="font-bold text-base mb-2">Generate Report</h3>
          <p className="text-sm text-slate-600 mb-4">
            Click below to start AI analysis and generate the full report. This takes 2–3 minutes.
          </p>
          <Button
            onClick={() => {
              if (!reportTriggeredRef.current) {
                reportTriggeredRef.current = true;
                handleFullReportGeneration();
              }
            }}
            disabled={reportGenerating || loading}
            className="w-full"
            style={{ backgroundColor: '#A8A67A' }}
          >
            {reportGenerating || loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Generating report… (2–3 minutes)
              </>
            ) : (
              'Generate Report'
            )}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>JAADE - Valuation Report - Fresh Clinics (v1)</CardTitle>
        <CardDescription>
          Quarter: Q{quarter} {year} - Step {currentStep} of {steps.length}
        </CardDescription>
        
        {/* Progress Steps */}
        <div className="flex items-center justify-between mt-4">
          {steps.map((step, index) => (
            <div key={step.num} className="flex items-center">
              <div className={`flex flex-col items-center ${currentStep >= step.num ? 'text-blue-600' : 'text-slate-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                  currentStep >= step.num ? 'border-blue-600 bg-blue-50' : 'border-slate-300'
                }`}>
                  {currentStep > step.num ? <CheckCircle2 className="w-5 h-5" /> : step.num}
                </div>
                <span className="text-xs mt-1 text-center">{step.title}</span>
              </div>
              {index < steps.length - 1 && (
                <div className={`h-0.5 w-12 mx-2 ${currentStep > step.num ? 'bg-blue-600' : 'bg-slate-300'}`}></div>
              )}
            </div>
          ))}
        </div>
      </CardHeader>

      <CardContent>
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2ValuationReview()}
        {currentStep === 3 && renderStep3()}
        {currentStep === 4 && renderStep4()}
        {currentStep === 5 && renderStep5()}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 1 || (currentStep === 5 && reportGenerating)}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          
          {currentStep < steps.length && (
            <Button
              onClick={nextStep}
              disabled={!canProceedToNextStep() || loading || extracting}
            >
              {extracting && currentStep === 1 && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              {extracting && currentStep === 1 ? 'Extracting Data...' : 'Next'}
              {!extracting && <ChevronRight className="w-4 h-4 ml-2" />}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default FreshClinicsReportFormV1;
